import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { UnifiedModal, ModalButton } from '@/components/common/UnifiedModal';
import { useThemeColors, useThemedStyles } from '@/hooks/residents/useThemeColors';
import { useAppSelector } from '@/store';
import { selectCalculatedFees } from '@/store/selectors/paymentsSelectors';
import { usePaymentBalance } from '@/features/payments/api/paymentHooks';
import { generatePaymentSummaryRows } from '@/utils/residents/paymentUtils';
import { useToast } from '@/contexts/admin/ToastContext';

type Params = {
  leaseId: number;
  amount: number;
  payFullAmount: boolean;
  paymentMethod?: any;
  onBehalfOfName?: string;
  suppressOverlay?: boolean;
};

type ConfirmInlineProps = Partial<Params> & {
  onRequestClose?: () => void;
};

export const ConfirmPaymentDetailsModal: React.FC<ConfirmInlineProps> = (props) => {
  const styles = useConfirmPaymentStyles();
  const route = useRoute<any>();
  const navigation = useNavigation<any>();
  const routeParams = (route.params || {}) as Params;
  
  // Get props or route params
  const leaseId = props.leaseId ?? routeParams.leaseId;
  const amount = props.amount ?? routeParams.amount;
  const payFullAmount = props.payFullAmount ?? routeParams.payFullAmount;
  const paymentMethod = props.paymentMethod ?? routeParams.paymentMethod;
  const onBehalfOfName = props.onBehalfOfName ?? routeParams.onBehalfOfName;
  const suppressOverlay = props.suppressOverlay ?? routeParams.suppressOverlay;
  
  // Get calculated fees from Redux store
  const calculatedFees = useAppSelector(selectCalculatedFees);
  const authUser = useAppSelector((s) => (s as any).auth?.user);
  
  // Fresh balance refetch for zero-balance verification when paying full balance
  const { refetch: refetchBalance } = usePaymentBalance(leaseId as number, { skip: true });
  const { showError } = useToast();

  // Determine if this is owner payment
  const isOwner = useMemo(() => {
    return !onBehalfOfName || onBehalfOfName.trim() === `${authUser?.firstName || ''} ${authUser?.lastName || ''}`.trim();
  }, [onBehalfOfName, authUser]);

  // Generate payment summary rows matching web implementation
  const summaryRows = useMemo(() => {
    if (!calculatedFees) return [];
    
    return generatePaymentSummaryRows(
      isOwner,
      onBehalfOfName || 'Resident',
      paymentMethod,
      Number(amount || 0),
      calculatedFees.convenienceFee,
      calculatedFees.total
    );
  }, [isOwner, onBehalfOfName, paymentMethod, amount, calculatedFees]);

  const onClose = () => {
    if (props.onRequestClose) return props.onRequestClose();
    navigation.goBack();
  };

  const onConfirm = async () => {
    // Zero-balance verification before posting when paying full balance
    if (payFullAmount) {
      try {
        const res: any = await refetchBalance();
        const remoteBalanceDue = Number(
          (res?.data?.balanceDue ?? res?.data?.response?.balanceDue ?? 0) as number,
        );
        if (!(remoteBalanceDue > 0)) {
          showError('The resident does not owe a balance to be paid.');
          return;
        }
      } catch (_err) {
        // If refetch fails, fall back to local check below
      }
    }

    // Fallback/local guard and proceed - navigate directly to processing
    if (!payFullAmount || (payFullAmount && Number(amount || 0) > 0)) {
      navigation.replace('TransactionProcessing', {
        leaseId,
        amount,
        payFullAmount,
        paymentMethodId: paymentMethod?.id,
        residentId: authUser?.id,
      });
    } else {
      showError('The resident does not owe a balance to be paid.');
    }
  };

  const confirmFooter = (
    <View style={styles.footerRow}>
      <ModalButton label="Cancel" variant="secondary" onPress={onClose} />
      <ModalButton label="Make Payment" variant="primary" onPress={onConfirm} />
    </View>
  );

  return (
    <UnifiedModal
      visible={true}
      variant="dialog"
      dialogSize="compact"
      onClose={onClose}
      title="Please confirm your payment details."
      dismissOnBackdrop={!suppressOverlay}
      scrollable
      footer={confirmFooter}
    >
      {summaryRows.map(([label, value], index) => (
        <View key={index} style={styles.detailRow}>
          <Text style={styles.detailLabel}>{label}</Text>
          <Text style={styles.detailValue}>{value}</Text>
        </View>
      ))}
    </UnifiedModal>
  );
};

const useConfirmPaymentStyles = () =>
  useThemedStyles(({ colors }) =>
    StyleSheet.create({
      detailRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 8,
      },
      detailLabel: {
        fontSize: 14,
        color: colors.text.muted,
      },
      detailValue: {
        fontSize: 16,
        fontWeight: '700',
        color: colors.text.primary,
      },
      footerRow: {
        flexDirection: 'row',
        gap: 12,
      },
    }),
  );

export default ConfirmPaymentDetailsModal;


